import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronDown, ChevronUp, X } from 'lucide-react';

export default function CapabilitySearch({ capabilities, officers, onFilteredResults }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [requesterFilter, setRequesterFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let filtered = capabilities;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(c => c.type === typeFilter);
    }

    if (requesterFilter !== 'all') {
      filtered = filtered.filter(c => c.requested_by === requesterFilter);
    }

    onFilteredResults(filtered);
  }, [searchQuery, statusFilter, typeFilter, requesterFilter, capabilities]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setRequesterFilter('all');
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || requesterFilter !== 'all';

  const requesters = [...new Set(capabilities.map(c => c.requested_by).filter(Boolean))];

  return (
    <Card className="glass-panel border-none mb-4">
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input data-testid="issues-capabilitysearch-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search capabilities by name or description..."
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-slate-600 text-slate-100 bg-slate-800 hover:bg-slate-700"
            >
              {showFilters ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
              Filters
            </Button>
            {hasActiveFilters && (
              <Button data-testid="issues-clear-btn" variant="ghost" onClick={clearFilters} className="text-slate-400 hover:text-slate-200">
                <X className="w-4 h-4 mr-2" /> Clear
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t border-slate-800">
              <div className="space-y-1">
                <label className="text-xs text-slate-400">Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full h-9 rounded-md bg-slate-950 border border-slate-700 text-slate-200 text-sm px-2">
                  <option value="all">All Statuses</option>
                  <option value="requested">Requested</option>
                  <option value="in_development">In Development</option>
                  <option value="contracted">Contracted</option>
                  <option value="approved">Approved</option>
                  <option value="institutionalized">Institutionalized</option>
                  <option value="active">Active</option>
                  <option value="deprecated">Deprecated</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-400">Type</label>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full h-9 rounded-md bg-slate-950 border border-slate-700 text-slate-200 text-sm px-2">
                  <option value="all">All Types</option>
                  <option value="contracted">Contracted</option>
                  <option value="acquired">Acquired</option>
                  <option value="developed">Developed</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-400">Requested By</label>
                <select value={requesterFilter} onChange={(e) => setRequesterFilter(e.target.value)}
                  className="w-full h-9 rounded-md bg-slate-950 border border-slate-700 text-slate-200 text-sm px-2">
                  <option value="all">All Officers</option>
                  {requesters.map(email => {
                    const o = officers.find(off => off.user_email === email);
                    return <option key={email} value={email}>{o?.callsign || email}</option>;
                  })}
                </select>
              </div>
            </div>
          )}

          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 pt-1">
              {searchQuery && <Badge variant="outline" className="border-indigo-500/30 text-indigo-400">Search: "{searchQuery}"</Badge>}
              {statusFilter !== 'all' && <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">Status: {statusFilter.replace('_', ' ')}</Badge>}
              {typeFilter !== 'all' && <Badge variant="outline" className="border-purple-500/30 text-purple-400">Type: {typeFilter}</Badge>}
              {requesterFilter !== 'all' && (
                <Badge variant="outline" className="border-cyan-500/30 text-cyan-400">
                  Requester: {officers.find(o => o.user_email === requesterFilter)?.callsign || requesterFilter}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}